import {
  Banner,
  DataTable,
  DataTableProps,
  FetchRequestOptions,
  LimitlessPagination,
  OnSortFunction,
  SortProps,
  Text,
  useSetSelector,
} from '@lucidhq/lucidium';
import {
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';

import { useFetchData } from '@/api/apiHooks';
import { SkeletonBar } from '@/components';
import { useSetUrlParams } from '@/hooks/useSetUrlParams';
import { stringifyParams } from '@/utils/stringifyParams';

import { ErrorScreen, NoDataScreen } from './components';

export type PaginationType = {
  page_number: string | number;
  page_size: string | number;
};

export type RemoteDataTableRef = {
  refetch: () => void;
};

export interface DefaultQueryStringProps<TItem> {
  order_by?: string;
  order?: SortProps<TItem>['sortOrder'];
  page_number?: PaginationType['page_number'];
  page_size?: PaginationType['page_size'];
  [k: string]: any;
}

export interface RemoteDataTableProps<
  TResponse,
  TItem,
  TItemKey extends keyof TItem,
> {
  apiUrl: string;
  fetchOptions?: FetchRequestOptions;
  toData: (response: TResponse) => TItem[];
  rowKeySelector: TItemKey;
  tableRef?: MutableRefObject<RemoteDataTableRef | undefined>;
  children: ReactNode;
  isPaginated?: boolean | number;
  defaultQueryString?: DefaultQueryStringProps<TItem>;
  hasSelectableRows?: boolean;
  onSelectedRowsChange?: (values: TItem[TItemKey][]) => void;
  expansionRenderer?: DataTableProps<TItem, TItemKey>['expansionRenderer'];
  noDataComponent?: ReactNode;
  errorComponent?: ReactNode;
  tableLss?: DataTableProps<TItem, TItemKey>['lss'];
  onViewMore?: () => void;
  viewMoreLss?: DataTableProps<TItem, TItemKey>['lss'];
  canScrollHorizontally?: boolean;
  useCursorPagination?: boolean;
  useLocalParams?: boolean;
}

const DELAY = 200;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 10;
export const SKELETON_BAR_COUNT = 5;
const defaultPaginationParams: PaginationType = {
  page_number: DEFAULT_PAGE_NUMBER,
  page_size: DEFAULT_PAGE_SIZE,
};

export const RemoteDataTable = <
  TResponse,
  TItem,
  TItemKey extends keyof TItem,
>({
  children,
  apiUrl,
  fetchOptions,
  rowKeySelector,
  tableRef,
  isPaginated = true,
  toData,
  defaultQueryString = {},
  hasSelectableRows = false,
  onSelectedRowsChange,
  errorComponent,
  noDataComponent,
  expansionRenderer,
  tableLss,
  onViewMore,
  viewMoreLss,
  canScrollHorizontally,
  useCursorPagination = false,
  useLocalParams = false,
}: Readonly<RemoteDataTableProps<TResponse, TItem, TItemKey>>) => {
  const [selectedRowKeys, toggleRowSelection, setAllRowKeys] = useSetSelector<
    TItem,
    TItemKey
  >(new Set([]));
  const [expandedRowKeys, setExpandedRowKeys] = useSetSelector<TItem, TItemKey>(
    new Set([])
  );
  const [localParams, setLocalParams] = useState(defaultQueryString);
  const [params, setParams] = useLocalParams
    ? [localParams, setLocalParams]
    : useSetUrlParams();

  const urlWithParams = useMemo(() => {
    const urlParams = {
      ...defaultPaginationParams,
      ...defaultQueryString,
      ...params,
    };

    return apiUrl + '?' + stringifyParams(urlParams);
  }, [params, apiUrl, defaultQueryString]);

  const [debouncedUrl] = useDebounce(urlWithParams, DELAY);

  const {
    isLoading,
    data: response,
    error,
    refetch,
  } = useFetchData<TResponse>({
    url: debouncedUrl,
    ...fetchOptions,
  });

  useEffect(() => {
    if (tableRef) {
      tableRef.current = { refetch };
    }
  }, [tableRef, refetch]);

  useEffect(() => {
    if (typeof onSelectedRowsChange === 'function') {
      onSelectedRowsChange([...selectedRowKeys]);
    }
  }, [selectedRowKeys]); //eslint-disable-line

  if (error) {
    return <ErrorScreen error={error} errorComponent={errorComponent} />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col w-full overflow-hidden pt-5 gap-2.5">
        {Array.from({ length: SKELETON_BAR_COUNT }, (_, index) => (
          <SkeletonBar key={index} />
        ))}
      </div>
    );
  }

  if (!response) {
    return <Banner palette="warning">No response</Banner>;
  }

  const data = toData(response);

  if (!data?.length) {
    return <NoDataScreen noDataComponent={noDataComponent} />;
  }

  const pageSize = Number(
    params.page_size || defaultPaginationParams.page_size
  );
  const page = Number(
    params.page_number || defaultPaginationParams.page_number
  );

  const pageItems = data && data.length === pageSize ? data.length : 0;

  const shouldShowPagination =
    typeof isPaginated === 'number' ? data.length >= isPaginated : isPaginated;

  const sortBy =
    (params.order_by as DefaultQueryStringProps<TItem>['order_by']) ||
    defaultQueryString.order_by ||
    undefined;

  const sortOrder =
    (params.order as DefaultQueryStringProps<TItem>['order']) ||
    defaultQueryString.order ||
    undefined;

  const handleSort: OnSortFunction<TItem> = ({ sortBy, sortOrder }) => {
    if (typeof sortBy === 'string') {
      setParams({
        order_by: sortBy,
        order: sortOrder,
      });
    }
  };

  const toggleSelectAll: DataTableProps<TItem, TItemKey>['onSelectAllRows'] = (
    selectAll
  ) => {
    setAllRowKeys(selectAll ? data.map((x) => x[rowKeySelector]) : []);
  };

  return (
    <>
      <div className="rounded-[5px] border border-gray-300 mt-2.5 pr-7.5">
        <div className={`${canScrollHorizontally ? 'overflow-x-auto' : ''}`}>
          <DataTable
            rowKeySelector={rowKeySelector}
            data={data}
            sortBy={sortBy as keyof TItem}
            sortOrder={sortOrder}
            onSort={handleSort}
            lss={{ margin: '15px', ...tableLss }}
            onSelectRow={hasSelectableRows ? toggleRowSelection : undefined}
            selectedRowKeys={hasSelectableRows ? selectedRowKeys : undefined}
            onSelectAllRows={hasSelectableRows ? toggleSelectAll : undefined}
            onExpandRow={expansionRenderer ? setExpandedRowKeys : undefined}
            expandedRowKeys={expansionRenderer ? expandedRowKeys : undefined}
            expansionRenderer={expansionRenderer}
          >
            {children}
          </DataTable>
        </div>
        {onViewMore && (
          <div className="h-[39px] flex justify-end">
            <Text
              onClick={onViewMore}
              title="View More"
              lss={{
                p: 0,
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'none',
                overflowWrap: 'break-word',
                wordBreak: 'break-all',
                color: 'primary500',
                ':hover': {
                  textDecoration: 'underline',
                },
                ...viewMoreLss,
              }}
            >
              View More
            </Text>
          </div>
        )}
      </div>
      {shouldShowPagination && !onViewMore && (
        <div className="my-4">
          <LimitlessPagination
            page={page}
            pageSize={pageSize}
            pageItems={pageItems}
            onChange={({
              page = defaultPaginationParams.page_number,
              pageSize = defaultPaginationParams.page_size,
            }) => {
              setParams({
                ...params,
                page_number: page.toString(),
                page_size: pageSize.toString(),
                start_after:
                  useCursorPagination && Number(page) > 1
                    ? data[data.length - 1][rowKeySelector]?.toString()
                    : undefined,
              });
            }}
          />
        </div>
      )}
    </>
  );
};
